// Mirrors MegaPuppy.swift's pure-logic surface.
// Visual sprites and animation are deferred to the render layer (Plan 2).

export const NO_INDEX = -1;
export const NO_PUPPY = -1;

export interface MegaPuppySnapshot {
  index: number;
  row: number;
  col: number;
  puppy: number;
  isSuperMega: boolean;
  isSpecialMega: boolean;
}

export class MegaPuppy {
  index: number = NO_INDEX;
  row = 0;
  col = 0;
  puppy: number = NO_PUPPY;
  isSuperMega = false;
  isSpecialMega = false;
  touchBeganInMe = false;

  // Mirrors MegaPuppy.swift::createMegaPuppy's pure-state assignments.
  attachAt(atIndex: number, puppyNum: number, isSuper: boolean, numSquaresHoriz: number): void {
    this.index = atIndex;
    this.puppy = puppyNum;
    this.isSuperMega = isSuper;
    this.row = Math.floor(atIndex / numSquaresHoriz);
    this.col = atIndex % numSquaresHoriz;
  }

  // Mirrors MegaPuppy.swift::indexAdjacentToMegaPuppy (line 345). True iff
  // sqIndex is one of the 8 orthogonal cells flanking the 2x2 mega box
  // (corners are NOT adjacent for +50 purposes — matches iOS).
  indexAdjacentToMegaPuppy(sqIndex: number, numSquaresHoriz: number): boolean {
    if (this.index === NO_INDEX) return false;
    // Left edge of mega.
    if (this.col > 0 && (sqIndex === this.index - 1 || sqIndex === this.index + (numSquaresHoriz - 1))) {
      return true;
    }
    // Above mega.
    if (this.row > 0 && (sqIndex === this.index - numSquaresHoriz || sqIndex === this.index - (numSquaresHoriz - 1))) {
      return true;
    }
    // Right edge of mega.
    if (this.col < numSquaresHoriz - 2 && (sqIndex === this.index + 2 || sqIndex === this.index + (numSquaresHoriz + 2))) {
      return true;
    }
    // Below mega — iOS doesn't bound-check the bottom edge (the impossible
    // index falls past the board and simply won't equal sqIndex).
    if (sqIndex === this.index + (numSquaresHoriz * 2) || sqIndex === this.index + 1 + (numSquaresHoriz * 2)) {
      return true;
    }
    return false;
  }

  // Mirrors MegaPuppy.swift::indexInMegaPuppy.
  // The MP is a 2x2 starting at `index` with width `numSquaresHoriz`:
  //   index, index+1, index+horiz, index+1+horiz.
  indexInMegaPuppy(indexInMP: number, numSquaresHoriz: number): boolean {
    if (this.index === NO_INDEX) return false;
    return (
      indexInMP === this.index ||
      indexInMP === this.index + 1 ||
      indexInMP === this.index + numSquaresHoriz ||
      indexInMP === this.index + 1 + numSquaresHoriz
    );
  }

  // Mirrors MegaPuppy.swift::snapshot().
  snapshot(): MegaPuppySnapshot {
    return {
      index: this.index,
      row: this.row,
      col: this.col,
      puppy: this.puppy,
      isSuperMega: this.isSuperMega,
      isSpecialMega: this.isSpecialMega,
    };
  }

  static fromSnapshot(s: MegaPuppySnapshot): MegaPuppy {
    const mp = new MegaPuppy();
    mp.index = s.index;
    mp.row = s.row;
    mp.col = s.col;
    mp.puppy = s.puppy;
    mp.isSuperMega = s.isSuperMega;
    mp.isSpecialMega = s.isSpecialMega;
    return mp;
  }
}
